import { red } from '@bloomfieldrobotics/canopy';
import { FeatureCollection, Geometry } from 'geojson';
import { useDeepCompareMemo } from 'use-deep-compare';
import { createFeatureCollection } from '../../../map/utils/createFeatureCollection';
import { getGradientColor } from '../../GradientScale/utils/getGradientColor';
import {
  GPSCoordinates,
  Point,
  PointColorConfiguration,
  PointFeature,
  PointFeatureProperties,
} from '../types';

type GeoJsonPoint = Point & {
  gps: GPSCoordinates;
};

export const usePointGeoJsonFeatures = <P extends GeoJsonPoint = GeoJsonPoint>(
  pointData: P[],
  colorConfig: PointColorConfiguration<P>
): FeatureCollection<Geometry, PointFeatureProperties<P>> => {
  return useDeepCompareMemo<
    FeatureCollection<Geometry, PointFeatureProperties<P>>
  >(() => {
    const pointFeatures = pointData.map<PointFeature<P>>((point, index) => {
      let pointPropertyValue: number | null = 0;
      // Try to get the value from the point via the property in the color config
      try {
        pointPropertyValue = parseInt(point[colorConfig.property] as string);
      } catch (error) {
        console.error(
          `Error getting value for color config property ${String(
            colorConfig.property
          )} on point ${point.id}:`,
          error
        );
        pointPropertyValue = null;
      }

      let color = red[500]; // Error: pointPropertyValue is missing
      if (pointPropertyValue !== null) {
        // Calculate the color of the point
        color = getGradientColor(
          colorConfig.color,
          pointPropertyValue,
          colorConfig.min,
          colorConfig.max
        );
      }

      const featureProperties = Object.assign(
        { color: color, index: index },
        point
      );

      const newPointFeature: PointFeature<P> = {
        id: point.id,
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.gps.longitude, point.gps.latitude],
        },
        properties: featureProperties,
      };

      return newPointFeature;
    });

    return createFeatureCollection(pointFeatures);
  }, [
    pointData,
    colorConfig.color,
    colorConfig.min,
    colorConfig.max,
    colorConfig.property,
  ]);
};
